// src/components/ProductionDashboard/ProductionReworkQueue.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCcw, Wrench, CheckCircle2, Trash2,
  AlertTriangle, Clock, Filter, Package, Search, X
} from 'lucide-react';
import { productionApi } from '../api';

// --- API Service ---
const ReworkService = {
  getQueue: async (status) => {
    const params = status && status !== 'ALL' ? { status } : {};
    const res = await productionApi.get('/production/rework-queue', { params });
    return res.data;
  },
  startRework: async (id) => {
    const res = await productionApi.post(`/production/rework/${id}/start`);
    return res.data;
  },
  completeRework: async (id, data) => {
    const res = await productionApi.post(`/production/rework/${id}/complete`, data);
    return res.data;
  },
  scrapRework: async (id, data) => {
    const res = await productionApi.post(`/production/rework/${id}/scrap`, data);
    return res.data;
  },
};

// --- Skeleton Loader ---
const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 8 }).map((_, i) => (
      <td key={i} className="px-4 py-4">
        <div className="h-4 bg-slate-200 rounded w-full" />
      </td>
    ))}
  </tr>
);

// --- Empty State ---
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
      <CheckCircle2 size={40} className="text-emerald-400" />
    </div>
    <h3 className="text-lg font-bold text-slate-700 mb-1">No Rework Items</h3>
    <p className="text-sm text-slate-400 max-w-sm">
      All clear! There are no pending rework items in the production queue.
    </p>
  </div>
);

// --- Status Badge ---
const StatusBadge = ({ status }) => {
  const cfg = {
    OPEN: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
    IN_REWORK: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    REWORK_DONE: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
    SCRAPPED: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  };
  const c = cfg[status] || cfg.OPEN;
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${c.bg} ${c.text} ${c.border}`}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
};

// --- Priority Badge ---
const PriorityBadge = ({ priority }) => {
  if (priority === 'high' || priority === 'urgent') {
    return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-50 text-red-600 border border-red-200">High</span>;
  }
  return null;
};

// --- Scrap Modal ---
const ScrapModal = ({ item, onClose, onConfirm, loading }) => {
  const [weight, setWeight] = useState('');
  const [pieces, setPieces] = useState(item?.pieces_to_rework || 1);
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = () => {
    if (!weight || parseFloat(weight) <= 0) { alert('Enter scrap weight'); return; }
    if (pieces <= 0) { alert('Enter pieces count'); return; }
    if (!confirmed) { alert('Please check the confirmation box'); return; }
    onConfirm({ scrap_weight_grams: parseFloat(weight), pieces_scrapped: pieces });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="bg-red-50 px-6 py-4 rounded-t-2xl border-b border-red-100 flex items-center justify-between">
          <h3 className="font-bold text-red-800 flex items-center gap-2">
            <Trash2 size={18} /> Send to Scrap
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-red-100 rounded-lg transition-colors">
            <X size={18} className="text-red-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 border border-slate-200">
            <div className="font-bold text-slate-800">{item?.job_card_no} &bull; {item?.stage_name}</div>
            <div className="text-xs text-slate-400 mt-1">{item?.design_name}</div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Scrap Weight (grams)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={weight}
              onChange={e => setWeight(e.target.value)}
              placeholder="e.g. 12.50"
              className="w-full p-3 border border-slate-300 rounded-lg text-lg font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Pieces Scrapped</label>
            <input
              type="number"
              min="1"
              value={pieces}
              onChange={e => setPieces(parseInt(e.target.value) || 0)}
              className="w-full p-3 border border-slate-300 rounded-lg text-lg font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
            />
          </div>

          <label className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-red-600"
            />
            <span className="text-xs text-amber-800 leading-relaxed">
              I confirm these pieces cannot be reworked and should be sent to the scrap/melt pool.
              This action cannot be undone.
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !confirmed}
              className="flex-1 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Confirm Scrap
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
export default function ProductionReworkQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // rework_id being acted on
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [scrapItem, setScrapItem] = useState(null);
  const [scrapLoading, setScrapLoading] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ReworkService.getQueue(filter);
      setItems(data.items || []);
    } catch (err) {
      console.error('Rework queue fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleStart = async (id) => {
    setActionLoading(id);
    try {
      await ReworkService.startRework(id);
      await fetchQueue();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to start rework');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (id) => {
    const notes = prompt('Rework notes (optional):');
    if (notes === null) return; // cancelled
    setActionLoading(id);
    try {
      await ReworkService.completeRework(id, { notes });
      await fetchQueue();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to complete rework');
    } finally {
      setActionLoading(null);
    }
  };

  const handleScrapConfirm = async (scrapData) => {
    if (!scrapItem) return;
    setScrapLoading(true);
    try {
      await ReworkService.scrapRework(scrapItem.rework_id, scrapData);
      setScrapItem(null);
      await fetchQueue();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to scrap pieces');
    } finally {
      setScrapLoading(false);
    }
  };

  const getRowBg = (age) => {
    if (age > 7) return 'bg-red-50 hover:bg-red-100/70';
    if (age > 3) return 'bg-orange-50 hover:bg-orange-100/70';
    return 'bg-white hover:bg-slate-50';
  };

  const filtered = items.filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (item.job_card_no || '').toLowerCase().includes(q) ||
      (item.design_name || '').toLowerCase().includes(q) ||
      (item.stage_name || '').toLowerCase().includes(q)
    );
  });

  const counts = {
    ALL: items.length,
    OPEN: items.filter(i => i.status === 'OPEN').length,
    IN_REWORK: items.filter(i => i.status === 'IN_REWORK').length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/production')} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
              <ArrowLeft size={20} className="text-slate-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Wrench size={22} className="text-orange-500" /> Rework Queue
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">{items.length} item{items.length !== 1 ? 's' : ''} in queue</p>
            </div>
          </div>
          <button
            onClick={fetchQueue}
            disabled={loading}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"
            title="Refresh"
          >
            <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Filter Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1">
            {['ALL', 'OPEN', 'IN_REWORK'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center gap-1.5 ${
                  filter === f
                    ? 'bg-slate-800 text-white'
                    : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Filter size={12} />
                {f === 'ALL' ? 'All' : f === 'OPEN' ? 'Pending Start' : 'In Rework'}
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  filter === f ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {counts[f] || 0}
                </span>
              </button>
            ))}
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search job card, design, stage..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Job Card</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Design</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Stage</th>
                  <th className="text-center px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Pieces</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Rejection Reason</th>
                  <th className="text-center px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Age</th>
                  <th className="text-center px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Status</th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold uppercase text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8}><EmptyState /></td>
                  </tr>
                ) : (
                  filtered.map(item => (
                    <tr key={item.rework_id} className={`border-b border-slate-100 transition-colors ${getRowBg(item.age_days)}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono font-bold text-slate-800 text-xs">{item.job_card_no}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-700 text-xs">{item.design_name || item.design_code || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-600">{item.stage_name}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-700">
                          <Package size={12} className="text-slate-400" /> {item.pieces_to_rework}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-600 max-w-[200px] truncate" title={item.rejection_reason}>
                          {item.rejection_reason || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold ${
                          item.age_days > 7 ? 'text-red-600' : item.age_days > 3 ? 'text-orange-600' : 'text-slate-500'
                        }`}>
                          <Clock size={11} /> {item.age_days}d
                          {item.age_days > 7 && <AlertTriangle size={11} />}
                        </span>
                        <PriorityBadge priority={item.priority} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 justify-end">
                          {item.status === 'OPEN' && (
                            <button
                              onClick={() => handleStart(item.rework_id)}
                              disabled={actionLoading === item.rework_id}
                              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              {actionLoading === item.rework_id ? (
                                <RefreshCcw size={11} className="animate-spin" />
                              ) : (
                                <Wrench size={11} />
                              )}
                              Start Rework
                            </button>
                          )}
                          {item.status === 'IN_REWORK' && (
                            <>
                              <button
                                onClick={() => handleComplete(item.rework_id)}
                                disabled={actionLoading === item.rework_id}
                                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold transition-colors disabled:opacity-50 flex items-center gap-1"
                              >
                                {actionLoading === item.rework_id ? (
                                  <RefreshCcw size={11} className="animate-spin" />
                                ) : (
                                  <CheckCircle2 size={11} />
                                )}
                                Complete
                              </button>
                              <button
                                onClick={() => setScrapItem(item)}
                                className="px-3 py-1.5 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 text-[11px] font-bold transition-colors flex items-center gap-1"
                              >
                                <Trash2 size={11} /> Scrap
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Age Legend */}
        {!loading && filtered.length > 0 && (
          <div className="mt-4 flex items-center gap-4 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-slate-200" /> Normal</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-50 border border-orange-200" /> &gt; 3 days</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200" /> &gt; 7 days (critical)</span>
          </div>
        )}
      </div>

      {/* Scrap Modal */}
      {scrapItem && (
        <ScrapModal
          item={scrapItem}
          onClose={() => setScrapItem(null)}
          onConfirm={handleScrapConfirm}
          loading={scrapLoading}
        />
      )}
    </div>
  );
}
